import type { SurgeriesState } from "./SurgeriesContext";
import { paths } from "@src/packages/openapi/schema";
import { RequestBody } from "@src/packages/openapi/helpers";
import type { TranslationFunction } from "@src/packages/i18n";
import { getSurgeryLabelKey, getSurgerySourceCodes } from "./options";

type PatchPayload = RequestBody<
  paths["/{tenantId}/patients/{patientId}"]["patch"]
>;
type MedicalHistoryPayload = NonNullable<PatchPayload["medicalHistory"]>;
type SurgeriesPayload = NonNullable<MedicalHistoryPayload["surgeries"]>;
type SurgeryPayload = SurgeriesPayload[number];
type MutableEntry = Record<string, unknown>;

export function transformSurgeriesStateToRequest(
  state: SurgeriesState,
  t: TranslationFunction,
): PatchPayload {
  const surgeriesPayload: MutableEntry[] = [];

  const idsToDeleteSet = new Set(state.surgeryIdsToDelete);

  // Add deletions
  state.surgeryIdsToDelete.forEach((id) => {
    if (id) {
      surgeriesPayload.push({ id, op: "delete" });
    }
  });

  // If user said "no", delete all existing entries
  if (state.hasSurgeries === false) {
    state.surgeries.forEach((s) => {
      if (s.id && !idsToDeleteSet.has(s.id)) {
        surgeriesPayload.push({ id: s.id, op: "delete" });
      }
    });

    return {
      medicalHistory: {
        hasSurgeries: false,
        ...(surgeriesPayload.length > 0
          ? { surgeries: surgeriesPayload as SurgeriesPayload }
          : {}),
      },
    };
  }

  // Add active surgery entries
  state.surgeries
    .filter((s) => !s.id || !idsToDeleteSet.has(s.id))
    .forEach((s) => {
      const sourceCodes = getSurgerySourceCodes(
        s.surgery,
        t(getSurgeryLabelKey(s.surgery)),
      );
      const entry = {
        surgery: s.surgery,
        ...(s.id ? { id: s.id } : {}),
        ...(s.other != null ? { other: s.other } : {}),
        ...(s.date != null ? { date: s.date } : {}),
        ...(s.comments != null ? { comments: s.comments } : {}),
        ...(sourceCodes.length > 0 ? { sourceCodes } : {}),
      } satisfies SurgeryPayload;
      surgeriesPayload.push(entry);
    });

  const medicalHistory: MedicalHistoryPayload = {
    hasSurgeries: state.hasSurgeries ?? null,
  };

  if (surgeriesPayload.length > 0) {
    medicalHistory.surgeries = surgeriesPayload as SurgeriesPayload;
  }

  return { medicalHistory };
}
